/**
 * Neurula Reporting Service
 * 
 * Comprehensive service for integrating with the Neurula reporting microservice.
 * Handles all reporting operations including standard reports, AI reports, 
 * templates, filters, and exports.
 */

import { BaseApiService } from './baseApiService';
import apiClient from './apiClient';
import { AUTH_CONFIG } from '../config/apiConfig';

class ReportingService extends BaseApiService {
    constructor() {
        // Configurable reporting service URL with fallback
        const reportingBaseUrl = process.env.REACT_APP_REPORTING_API_URL || 'http://localhost:8001/api/v1';
        super(reportingBaseUrl);
        this.reportingBaseUrl = reportingBaseUrl;
        
        // Add authentication token to all requests
        this.setupAuthInterceptor();
    }
    
    /**
     * Setup authentication interceptor for all requests
     */
    setupAuthInterceptor() {
        if (apiClient.interceptors) {
            // Request interceptor for auth
            apiClient.interceptors.request.use(
                (config) => {
                    // Use the same token retrieval method as auth.service.js
                    const token = this.getAccessToken();
                    if (token) {
                        config.headers.Authorization = `Bearer ${token}`;
                    }
                    return config;
                },
                (error) => Promise.reject(error)
            );

            // Response interceptor for error handling
            apiClient.interceptors.response.use(
                (response) => response,
                (error) => {
                    // Handle common HTTP errors
                    if (error.response) {
                        const { status, data } = error.response;
                        
                        switch (status) {
                            case 401:
                                // Unauthorized - redirect to login or refresh token
                                // Could dispatch logout action here
                                break;
                            case 403:
                                break;
                            case 404:
                                break;
                            case 429:
                                break;
                            case 500:
                                break;
                            case 503:
                                break;
                            default:
                        }
                    } else if (error.request) {
                        // Network error
                    } else {
                    }
                    
                    return Promise.reject(error);
                }
            );
        }
    }

    /**
     * Get access token from localStorage (same method as auth.service.js)
     * @returns {string|null} - JWT access token
     */
    getAccessToken() {
        try {
            const authData = localStorage.getItem(AUTH_CONFIG.TOKEN_STORAGE_KEY);
            if (authData) {
                const parsed = JSON.parse(authData);
                return parsed.authToken || parsed.access_token;
            }
        } catch (error) {
        }
        return null;
    }

    // ============================================================================
    // REPORTS OPERATIONS
    // ============================================================================

    /**
     * Get list of reports with pagination and filtering
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} - API response
     */
    async getReports(params = {}) {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/reports`, { params });
            // Handle both direct data and wrapped response formats
            const data = response.data?.data || response.data;
            return this.createSuccessResponse(data, response.data?.message || 'Reports retrieved successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Generate a new report using backend schema (MAIN METHOD)
     * @param {Object} reportGenerationRequest - Report generation request matching backend schema
     * @returns {Promise<Object>} - API response
     */
    async generateReportFromTemplate(reportGenerationRequest) {
        try {
            this.validateRequiredFields(reportGenerationRequest, ['template_id']);
            const response = await apiClient.post(`${this.reportingBaseUrl}/reports/generate`, reportGenerationRequest);
            return this.createSuccessResponse(response.data, response.data.message || 'Report generation started');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Create a new report (legacy method, deprecated)
     * @param {Object} reportData - Report creation data
     * @returns {Promise<Object>} - API response
     */
    async createReport(reportData) {
        try {
            this.validateRequiredFields(reportData, ['name', 'type']);
            const response = await apiClient.post(`${this.reportingBaseUrl}/reports`, reportData);
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get report by ID
     * @param {string} reportId - Report ID
     * @returns {Promise<Object>} - API response
     */
    async getReportById(reportId) {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/reports/${reportId}`);
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Update report
     * @param {string} reportId - Report ID
     * @param {Object} updateData - Update data
     * @returns {Promise<Object>} - API response
     */
    async updateReport(reportId, updateData) {
        try {
            const response = await apiClient.put(`${this.reportingBaseUrl}/reports/${reportId}`, updateData);
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Delete report
     * @param {string} reportId - Report ID
     * @returns {Promise<Object>} - API response
     */
    async deleteReport(reportId) {
        try {
            await apiClient.delete(`${this.reportingBaseUrl}/reports/${reportId}`);
            return this.createSuccessResponse(null, 'Report deleted successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Generate report data
     * @param {string} reportId - Report ID
     * @param {Object} params - Generation parameters
     * @returns {Promise<Object>} - API response
     */
    async generateReport(reportId, params = {}) {
        try {
            const response = await apiClient.post(`${this.reportingBaseUrl}/reports/${reportId}/generate`, params);
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get report status
     * @param {string} reportId - Report ID
     * @returns {Promise<Object>} - API response
     */
    async getReportStatus(reportId) {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/reports/${reportId}/status`);
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get report history
     * @param {string} reportId - Report ID
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} - API response
     */
    async getReportHistory(reportId, params = {}) {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/reports/${reportId}/history`, { params });
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Download report file (matches backend /reports/{report_id}/download endpoint)
     * @param {string} reportId - Report ID
     * @returns {Promise<Object>} - API response with file blob
     */
    async downloadReport(reportId) {
        try {
            const response = await apiClient.get(
                `${this.reportingBaseUrl}/reports/${reportId}/download`,
                { responseType: 'blob' }
            );
            return this.createSuccessResponse(response.data, 'Report downloaded successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get report data (JSON format)
     * @param {string} reportId - Report ID
     * @returns {Promise<Object>} - API response
     */
    async getReportData(reportId) {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/reports/${reportId}/data`);
            return this.createSuccessResponse(response.data, 'Report data retrieved successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Cancel report generation
     * @param {string} reportId - Report ID
     * @returns {Promise<Object>} - API response
     */
    async cancelReport(reportId) {
        try {
            const response = await apiClient.post(`${this.reportingBaseUrl}/reports/${reportId}/cancel`);
            return this.createSuccessResponse(response.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    // ============================================================================
    // TEMPLATES OPERATIONS
    // ============================================================================

    /**
     * Get list of templates with pagination and filtering
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} - API response
     */
    async getTemplates(params = {}) {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/templates`, { params });
            // Handle both direct data and wrapped response formats
            const data = response.data?.data || response.data;
            return this.createSuccessResponse(data, response.data?.message || 'Templates retrieved successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Create a new template
     * @param {Object} templateData - Template creation data
     * @returns {Promise<Object>} - API response
     */
    async createTemplate(templateData) {
        try {
            this.validateRequiredFields(templateData, ['name', 'type']);
            const response = await apiClient.post(`${this.reportingBaseUrl}/templates`, templateData);
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get template by ID
     * @param {string} templateId - Template ID
     * @returns {Promise<Object>} - API response
     */
    async getTemplateById(templateId) {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/templates/${templateId}`);
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Update template
     * @param {string} templateId - Template ID
     * @param {Object} updateData - Update data
     * @returns {Promise<Object>} - API response
     */
    async updateTemplate(templateId, updateData) {
        try {
            const response = await apiClient.put(`${this.reportingBaseUrl}/templates/${templateId}`, updateData);
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Delete template
     * @param {string} templateId - Template ID
     * @returns {Promise<Object>} - API response
     */
    async deleteTemplate(templateId) {
        try {
            await apiClient.delete(`${this.reportingBaseUrl}/templates/${templateId}`);
            return this.createSuccessResponse(null, 'Template deleted successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Clone template
     * @param {string} templateId - Template ID
     * @param {Object} cloneData - Clone configuration
     * @returns {Promise<Object>} - API response
     */
    async cloneTemplate(templateId, cloneData = {}) {
        try {
            const response = await apiClient.post(`${this.reportingBaseUrl}/templates/${templateId}/clone`, cloneData);
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Preview template
     * @param {string} templateId - Template ID
     * @param {Object} previewData - Preview data
     * @returns {Promise<Object>} - API response
     */
    async previewTemplate(templateId, previewData = {}) {
        try {
            const response = await apiClient.post(`${this.reportingBaseUrl}/templates/${templateId}/preview`, previewData);
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    // ============================================================================
    // FILTERS OPERATIONS
    // ============================================================================

    /**
     * Get list of filters
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} - API response
     */
    async getFilters(params = {}) {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/filters`, { params });
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Create a new filter
     * @param {Object} filterData - Filter creation data
     * @returns {Promise<Object>} - API response
     */
    async createFilter(filterData) {
        try {
            this.validateRequiredFields(filterData, ['name', 'conditions']);
            const response = await apiClient.post(`${this.reportingBaseUrl}/filters`, filterData);
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get filter by ID
     * @param {string} filterId - Filter ID
     * @returns {Promise<Object>} - API response
     */
    async getFilterById(filterId) {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/filters/${filterId}`);
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Update filter
     * @param {string} filterId - Filter ID
     * @param {Object} updateData - Update data
     * @returns {Promise<Object>} - API response
     */
    async updateFilter(filterId, updateData) {
        try {
            const response = await apiClient.put(`${this.reportingBaseUrl}/filters/${filterId}`, updateData);
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Delete filter
     * @param {string} filterId - Filter ID
     * @returns {Promise<Object>} - API response
     */
    async deleteFilter(filterId) {
        try {
            await apiClient.delete(`${this.reportingBaseUrl}/filters/${filterId}`);
            return this.createSuccessResponse(null, 'Filter deleted successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Apply filter to test data
     * @param {string} filterId - Filter ID
     * @param {Object} testData - Test data
     * @returns {Promise<Object>} - API response
     */
    async applyFilter(filterId, testData = {}) {
        try {
            const response = await apiClient.post(`${this.reportingBaseUrl}/filters/${filterId}/apply`, testData);
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    // ============================================================================
    // AI REPORTS OPERATIONS
    // ============================================================================

    /**
     * Generate AI report from natural language (matches backend AI_SERVICE_DOCUMENTATION)
     * @param {Object} aiReportData - AI report generation data matching AIReportRequest schema
     * @returns {Promise<Object>} - API response
     */
    async generateAIReport(aiReportData) {
        try {
            this.validateRequiredFields(aiReportData, ['query']);
            const response = await apiClient.post(`${this.reportingBaseUrl}/ai/generate`, aiReportData);
            // Backend AI service returns direct data, not wrapped in data.data
            return this.createSuccessResponse(response.data, response.data.message || 'AI report generated successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get AI report suggestions (matches backend GET /api/v1/ai/suggestions)
     * @param {Object} context - Optional context parameters
     * @returns {Promise<Object>} - API response
     */
    async getAISuggestions(context = {}) {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/ai/suggestions`);
            // Backend returns direct suggestions data
            return this.createSuccessResponse(response.data, 'AI suggestions retrieved successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Enhance existing report with AI
     * @param {string} reportId - Report ID
     * @param {Object} enhanceData - Enhancement parameters
     * @returns {Promise<Object>} - API response
     */
    async enhanceReportWithAI(reportId, enhanceData = {}) {
        try {
            const response = await apiClient.post(`${this.reportingBaseUrl}/ai/enhance/${reportId}`, enhanceData);
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Interpret AI query for template suggestions (matches backend POST /api/v1/ai/interpret)
     * @param {Object} queryData - Query interpretation data with 'prompt' field
     * @returns {Promise<Object>} - API response
     */
    async interpretAIQuery(queryData) {
        try {
            this.validateRequiredFields(queryData, ['query']);
            const response = await apiClient.post(`${this.reportingBaseUrl}/ai/test-intent`, queryData);
            // Backend returns direct interpretation data
            return this.createSuccessResponse(response.data, 'Query interpreted successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get AI example prompts (matches backend GET /api/v1/ai/examples)
     * @param {string} category - Optional category filter (patient, medpass, facility, etc.)
     * @returns {Promise<Object>} - API response
     */
    async getAIExamples(category = null) {
        try {
            const params = category ? { category } : {};
            const response = await apiClient.get(`${this.reportingBaseUrl}/ai/examples`, { params });
            // Backend returns direct examples data
            return this.createSuccessResponse(response.data, 'AI examples retrieved successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    // ============================================================================
    // EXPORT OPERATIONS
    // ============================================================================

    /**
     * Export report in specified format
     * @param {string} reportId - Report ID
     * @param {Object} exportOptions - Export configuration
     * @returns {Promise<Object>} - API response
     */
    async exportReport(reportId, exportOptions = {}) {
        try {
            const { format = 'pdf', ...options } = exportOptions;
            const response = await apiClient.post(
                `${this.reportingBaseUrl}/exports/${reportId}/${format}`,
                { ...options },
                { responseType: format === 'pdf' ? 'blob' : 'json' }
            );
            
            if (format === 'pdf') {
                return this.createSuccessResponse(response.data, 'Report exported successfully');
            }
            
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Export multiple reports
     * @param {Array} reportIds - Array of report IDs
     * @param {Object} exportOptions - Export configuration
     * @returns {Promise<Object>} - API response
     */
    async exportMultipleReports(reportIds, exportOptions = {}) {
        try {
            const response = await apiClient.post(`${this.reportingBaseUrl}/exports/bulk`, {
                report_ids: reportIds,
                ...exportOptions
            });
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get export status
     * @param {string} exportId - Export ID
     * @returns {Promise<Object>} - API response
     */
    async getExportStatus(exportId) {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/exports/${exportId}/status`);
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Download exported file
     * @param {string} exportId - Export ID
     * @returns {Promise<Object>} - API response
     */
    async downloadExport(exportId) {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/exports/${exportId}/download`, {
                responseType: 'blob'
            });
            return this.createSuccessResponse(response.data, 'File downloaded successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    // ============================================================================
    // BULK OPERATIONS
    // ============================================================================

    /**
     * Bulk delete reports
     * @param {Array} reportIds - Array of report IDs
     * @returns {Promise<Object>} - API response
     */
    async bulkDeleteReports(reportIds) {
        try {
            const response = await apiClient.post(`${this.reportingBaseUrl}/reports/bulk-delete`, {
                report_ids: reportIds
            });
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Bulk update report status
     * @param {Array} reportIds - Array of report IDs
     * @param {string} status - New status
     * @returns {Promise<Object>} - API response
     */
    async bulkUpdateReportStatus(reportIds, status) {
        try {
            const response = await apiClient.post(`${this.reportingBaseUrl}/reports/bulk-update-status`, {
                report_ids: reportIds,
                status
            });
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    // ============================================================================
    // UTILITY METHODS
    // ============================================================================

    /**
     * Search reports across all criteria
     * @param {Object} searchParams - Search parameters
     * @returns {Promise<Object>} - API response
     */
    async searchReports(searchParams) {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/reports/search`, { params: searchParams });
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get system statistics
     * @returns {Promise<Object>} - API response
     */
    async getSystemStats() {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/stats`);
            // Handle both direct data and wrapped response formats
            const data = response.data?.data || response.data;
            return this.createSuccessResponse(data, response.data?.message || 'System statistics retrieved successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get available report types
     * @returns {Promise<Object>} - API response
     */
    async getReportTypes() {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/report-types`);
            // Handle both direct data and wrapped response formats
            const data = response.data?.data || response.data;
            return this.createSuccessResponse(data, response.data?.message || 'Report types retrieved successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Validate report configuration
     * @param {Object} reportConfig - Report configuration
     * @returns {Promise<Object>} - API response
     */
    async validateReportConfig(reportConfig) {
        try {
            const response = await apiClient.post(`${this.reportingBaseUrl}/validate`, reportConfig);
            return this.createSuccessResponse(response.data.data, response.data.message);
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    // ============================================================================
    // EMR INTEGRATION OPERATIONS
    // ============================================================================

    /**
     * Get EMR departments
     * @returns {Promise<Object>} - API response
     */
    async getEMRDepartments() {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/emr/departments`);
            return this.createSuccessResponse(response.data, 'EMR departments retrieved successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get EMR categories
     * @param {string} departmentId - Optional department filter
     * @returns {Promise<Object>} - API response
     */
    async getEMRCategories(departmentId = null) {
        try {
            const params = departmentId ? { department_id: departmentId } : {};
            const response = await apiClient.get(`${this.reportingBaseUrl}/emr/categories`, { params });
            return this.createSuccessResponse(response.data, 'EMR categories retrieved successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get EMR forms
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} - API response
     */
    async getEMRForms(params = {}) {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/emr/forms`, { params });
            return this.createSuccessResponse(response.data, 'EMR forms retrieved successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get EMR form by ID
     * @param {string} formId - Form ID
     * @returns {Promise<Object>} - API response
     */
    async getEMRFormById(formId) {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/emr/forms/${formId}`);
            return this.createSuccessResponse(response.data, 'EMR form retrieved successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get form submissions
     * @param {string} formId - Form ID
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} - API response
     */
    async getFormSubmissions(formId, params = {}) {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/emr/forms/${formId}/submissions`, { params });
            return this.createSuccessResponse(response.data, 'Form submissions retrieved successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Generate template from EMR form
     * @param {string} formId - Form ID
     * @returns {Promise<Object>} - API response
     */
    async generateTemplateFromForm(formId) {
        try {
            const response = await apiClient.post(`${this.reportingBaseUrl}/emr/forms/${formId}/generate-template`);
            return this.createSuccessResponse(response.data, 'Template generated successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Generate all form templates
     * @param {boolean} forceRegenerate - Force regeneration of existing templates
     * @returns {Promise<Object>} - API response
     */
    async generateAllFormTemplates(forceRegenerate = false) {
        try {
            const params = { force_regenerate: forceRegenerate };
            const response = await apiClient.post(`${this.reportingBaseUrl}/emr/generate-all-templates`, {}, { params });
            return this.createSuccessResponse(response.data, 'Template generation started');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get form analytics
     * @param {string} formId - Form ID
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} - API response
     */
    async getFormAnalytics(formId, params = {}) {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/emr/forms/${formId}/analytics`, { params });
            return this.createSuccessResponse(response.data, 'Form analytics retrieved successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get patient form submissions (uses main EMR backend)
     * @param {string} patientId - Patient ID
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} - API response
     */
    async getPatientFormSubmissions(patientId, params = {}) {
        try {
            // Use the main EMR backend for form submissions
            const emrBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
            const response = await apiClient.get(`${emrBaseUrl}/dynamic-forms/submissions`, { 
                params: { ...params, patient_id: patientId }
            });
            return this.createSuccessResponse(response.data, 'Patient form submissions retrieved successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get EMR sync status
     * @returns {Promise<Object>} - API response
     */
    async getEMRSyncStatus() {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/emr/sync-status`);
            return this.createSuccessResponse(response.data, 'EMR sync status retrieved successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    // ============================================================================
    // PATIENT OPERATIONS FOR FILTERING (Integration with main EMR backend)
    // ============================================================================

    /**
     * Get list of patients for filtering (uses main EMR backend, not reporting service)
     * @param {Object} params - Query parameters (search, limit, etc.)
     * @returns {Promise<Object>} - API response
     */
    async getPatients(params = {}) {
        try {
            // Use the main EMR backend patient endpoint, not reporting service
            const emrBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
            const response = await apiClient.get(`${emrBaseUrl}/patients`, { params });
            
            // Transform response to match expected format
            const transformedData = {
                patients: response.data.patients || response.data || [],
                total: response.data.total || 0,
                page: response.data.page || 1,
                per_page: response.data.per_page || 50
            };
            
            return this.createSuccessResponse(transformedData, 'Patients retrieved successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Search patients by name or ID (uses main EMR backend, not reporting service)
     * @param {string} searchTerm - Search term
     * @param {number} limit - Maximum number of results (default: 20)
     * @returns {Promise<Object>} - API response
     */
    async searchPatients(searchTerm, limit = 20) {
        try {
            const params = { 
                query: searchTerm, 
                limit: limit,
                search_fields: ['name', 'mrn', 'emirates_id', 'phone']
            };
            
            // Use the main EMR backend patient search endpoint
            const emrBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
            const response = await apiClient.get(`${emrBaseUrl}/patients/search`, { params });
            
            // Transform response to match expected format for reporting
            const transformedData = {
                patients: response.data.patients || response.data || [],
                total_results: response.data.total_results || 0,
                search_query: searchTerm
            };
            
            return this.createSuccessResponse(transformedData, 'Patient search completed successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get patient by ID (uses main EMR backend, not reporting service)
     * @param {string} patientId - Patient ID
     * @returns {Promise<Object>} - API response
     */
    async getPatientById(patientId) {
        try {
            // Use the main EMR backend patient endpoint
            const emrBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
            const response = await apiClient.get(`${emrBaseUrl}/patients/${patientId}`);
            return this.createSuccessResponse(response.data, 'Patient retrieved successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get filter suggestions for a specific template
     * @param {string} templateId - Template ID
     * @returns {Promise<Object>} - API response with suggested filter values
     */
    async getTemplateFilterSuggestions(templateId) {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/templates/${templateId}/filter-suggestions`);
            return this.createSuccessResponse(response.data, 'Filter suggestions retrieved successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    // ============================================================================
    // HEMODIALYSIS REPORTING OPERATIONS
    // ============================================================================

    /**
     * Get available hemodialysis report templates
     * @returns {Promise<Object>} - API response with hemodialysis templates
     */
    async getHemodialysisTemplates() {
        try {
            const response = await apiClient.get(`${this.reportingBaseUrl}/hemodialysis/templates`);
            return this.createSuccessResponse(response.data, 'Hemodialysis templates retrieved successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Generate single patient hemodialysis PDF report
     * @param {string} patientId - Patient ID for the report
     * @param {string} sessionDate - Optional specific session date (YYYY-MM-DD format)
     * @returns {Promise<Object>} - API response with PDF blob
     */
    async generateHemodialysisSinglePatientPDF(patientId, sessionDate = null) {
        try {
            this.validateRequiredFields({ patient_id: patientId }, ['patient_id']);
            
            const params = { patient_id: patientId };
            if (sessionDate) {
                params.session_date = sessionDate;
            }

            const response = await apiClient.get(
                `${this.reportingBaseUrl}/hemodialysis/report/pdf/single`,
                { 
                    params,
                    responseType: 'blob'
                }
            );
            
            return this.createSuccessResponse(response.data, 'Single patient hemodialysis report generated successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Generate multi-patient hemodialysis summary PDF report
     * @param {string} startDate - Start date for report (YYYY-MM-DD format)
     * @param {string} endDate - End date for report (YYYY-MM-DD format)
     * @param {Array<string>} patientIds - Optional array of specific patient IDs
     * @returns {Promise<Object>} - API response with PDF blob
     */
    async generateHemodialysisMultiPatientPDF(startDate = null, endDate = null, patientIds = null) {
        try {
            const params = {};
            
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            if (patientIds && patientIds.length > 0) {
                params.patient_ids = patientIds.join(',');
            }

            const response = await apiClient.get(
                `${this.reportingBaseUrl}/hemodialysis/report/pdf/multi`,
                { 
                    params,
                    responseType: 'blob'
                }
            );
            
            return this.createSuccessResponse(response.data, 'Multi-patient hemodialysis summary report generated successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get hemodialysis form submissions for a patient
     * @param {string} patientId - Patient ID
     * @param {Object} params - Optional query parameters (date range, etc.)
     * @returns {Promise<Object>} - API response with form submissions
     */
    async getHemodialysisFormSubmissions(patientId, params = {}) {
        try {
            this.validateRequiredFields({ patient_id: patientId }, ['patient_id']);
            
            // Use the main EMR backend for form submissions
            const emrBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
            const queryParams = { 
                ...params, 
                patient_id: patientId,
                // Filter for hemodialysis forms only
                form_ids: 'FORM-41563642,FORM-8591F1B3,FORM-BEC0F708'
            };

            const response = await apiClient.get(`${emrBaseUrl}/dynamic-forms/submissions`, { 
                params: queryParams
            });
            
            return this.createSuccessResponse(response.data, 'Hemodialysis form submissions retrieved successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Get hemodialysis report data (JSON format)
     * @param {string} patientId - Optional patient ID filter
     * @param {string} startDate - Optional start date filter
     * @param {string} endDate - Optional end date filter
     * @returns {Promise<Object>} - API response with hemodialysis session data
     */
    async getHemodialysisReportData(patientId = null, startDate = null, endDate = null) {
        try {
            const params = {};
            if (patientId) params.patient_id = patientId;
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;

            const response = await apiClient.get(`${this.reportingBaseUrl}/hemodialysis/report`, { params });
            return this.createSuccessResponse(response.data, 'Hemodialysis report data retrieved successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }

    /**
     * Generate custom hemodialysis PDF report with advanced options
     * @param {Object} reportParams - Custom report parameters
     * @returns {Promise<Object>} - API response with PDF blob
     */
    async generateCustomHemodialysisPDF(reportParams) {
        try {
            this.validateRequiredFields(reportParams, ['report_type']);
            
            const response = await apiClient.post(
                `${this.reportingBaseUrl}/hemodialysis/report/pdf/custom`,
                reportParams,
                { responseType: 'blob' }
            );
            
            return this.createSuccessResponse(response.data, 'Custom hemodialysis report generated successfully');
        } catch (error) {
            return this.handleApiError(error);
        }
    }
}

// Export singleton instance
const reportingService = new ReportingService();
export default reportingService;